import React, { useEffect, useState, useMemo } from "react";
import { 
  Box, Typography, Container, Card, CardContent, Grid, 
  CircularProgress, Divider, CardMedia, Tooltip, Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { db, storageRef } from "../../Environment/firebase";
import { MstaFontFamily } from "../../Functions/Style/MstaFont";
import { lightBlue, grey } from "@mui/material/colors";
import PersonIcon from "@mui/icons-material/Person";
import CreateIcon from "@mui/icons-material/Create";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import Carousel from "react-material-ui-carousel";
import { departments } from "../../Environment/Config";

export default function MStudyList() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("すべて");

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const snapshot = await db.collection("questions")
          .orderBy("createdAt", "desc")
          .get();
        let data = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter((q: any) => q.status === "published")
          .slice(0, 20);

        for (let i = 0; i < data.length; i++) {
          const q: any = data[i];
          if (q.imageUrls && q.imageUrls.length > 0) {
            const resolvedUrls = await Promise.all(
              q.imageUrls.map(async (urlPath: string) => {
                if (urlPath && !urlPath.startsWith("http")) {
                  try {
                    return await storageRef.child(urlPath).getDownloadURL();
                  } catch (e) {
                    console.warn("画像URLの取得に失敗:", urlPath);
                    return urlPath;
                  }
                }
                return urlPath;
              })
            );
            q.resolvedImageUrls = resolvedUrls;
          }
        }

        setQuestions(data);
      } catch (error) {
        console.error("Error fetching published questions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const availableDepartments = useMemo(() => {
    const depts = new Set<string>();
    questions.forEach((q) => {
      if (Array.isArray(q.departmentTags)) {
        q.departmentTags.forEach((tag: string) => depts.add(tag));
      } else if (typeof q.departmentTags === 'string') {
        depts.add(q.departmentTags);
      }
    });
    // Configに定義されている順序を保つため、元の配列をフィルターする
    return departments.filter(dept => depts.has(dept));
  }, [questions]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "95vh" }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1, pt: 3, pb: 4, minHeight: "95vh", fontFamily: MstaFontFamily, position: "relative" }}>
      <Typography
        gutterBottom
        align="left"
        variant="h4"
        fontFamily={MstaFontFamily}
      >
        問題演習
      </Typography>

      <Divider
        sx={{
          borderBottomWidth: 1.5,
          borderColor: "black",
          mt: 1,
          mb: 3,
        }}
      />

      {/* 診療科フィルター */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            label="すべて"
            onClick={() => setSelectedDepartment("すべて")}
            sx={{
              fontWeight: selectedDepartment === "すべて" ? "bold" : "normal",
              bgcolor: selectedDepartment === "すべて" ? lightBlue[400] : grey[200],
              color: selectedDepartment === "すべて" ? "white" : "black",
              "&:hover": {
                bgcolor: selectedDepartment === "すべて" ? lightBlue[500] : grey[300],
              },
            }}
          />
          {availableDepartments.map((dept) => (
            <Chip
              key={dept}
              label={dept}
              onClick={() => setSelectedDepartment(dept)}
              sx={{
                fontWeight: selectedDepartment === dept ? "bold" : "normal",
                bgcolor: selectedDepartment === dept ? lightBlue[400] : grey[200],
                color: selectedDepartment === dept ? "white" : "black",
                "&:hover": {
                  bgcolor: selectedDepartment === dept ? lightBlue[500] : grey[300],
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {questions.filter((q) => selectedDepartment === "すべて" || (q.departmentTags && q.departmentTags.includes(selectedDepartment))).length === 0 ? (
        <Typography variant="h6" color="text.secondary" textAlign="center" sx={{ mt: 5 }}>
          該当する問題はありません。
        </Typography>
      ) : (
        <Grid 
          display="grid" 
          justifyContent="center"
          gridTemplateColumns={{ sm: "1fr 1fr", xs: "1fr" }} 
          gap={{ xs: 2, md: 4 }}
          width="100%"
        >
          {questions
            .filter((q) => selectedDepartment === "すべて" || (q.departmentTags && q.departmentTags.includes(selectedDepartment)))
            .map((q) => (
            <Grid item style={{ width: "100%" }} key={q.id}>
              <Card
                sx={{
                  height: 350,
                  display: "flex",
                  border: 2,
                  alignItems: "center",
                  "&:hover": { cursor: "pointer" },
                }}
                onClick={() => navigate(`/mstudy/${q.id}`)}
              >
                <Grid container height={300} justifyContent={"space-between"}>
                  {/* ====== 左側：テキストエリア ====== */}
                  <Grid
                    item
                    xs={7}
                    md={6}
                    display="flex"
                    flexDirection="column"
                    alignItems={"flex-start"}
                    justifyContent={"space-between"}
                    gap={2}
                  >
                    <CardContent>
                      {q.questionNumber && (
                        <Typography variant="h6" color="textSecondary" fontSize={20} fontWeight={300} fontFamily={MstaFontFamily} sx={{ mb: 0.5, textAlign: "left" }}>
                          No.{q.questionNumber}
                        </Typography>
                      )}
                      <Grid display="flex" alignItems="center" justifyContent="start">
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          fontSize={20}
                          fontWeight={300}
                          fontFamily={MstaFontFamily}
                        >
                          {q.caseInfo?.age ? (q.caseInfo.age < 10 ? "10歳未満" : q.caseInfo.age >= 100 ? "100歳以上" : `${Math.floor(q.caseInfo.age / 10) * 10}代`) : "年齢不明"}
                        </Typography>
                        <Typography variant="h6" color="textSecondary" fontSize={20} fontWeight={300} fontFamily={MstaFontFamily}>/</Typography>
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          fontSize={20}
                          fontWeight={300}
                          fontFamily={MstaFontFamily}
                        >
                          {q.caseInfo?.gender === "male" ? "男性" : q.caseInfo?.gender === "female" ? "女性" : "その他"}
                        </Typography>
                        <Typography variant="h6" color="textSecondary" fontSize={20} fontWeight={300} fontFamily={MstaFontFamily}>/</Typography>
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          fontSize={20}
                          fontWeight={300}
                          fontFamily={MstaFontFamily}
                        >
                          設問数: {q.quizItems?.length || 0}問
                        </Typography>
                      </Grid>

                      <Tooltip title={q.caseInfo?.history || "主訴・症状なし"}>
                        <Typography
                          variant="h6"
                          color="text.primary"
                          textAlign="left"
                          fontFamily={MstaFontFamily}
                          fontWeight={600}
                          className="title"
                          sx={{
                            fontSize: { xs: 20, md: 20 },
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 6,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            mt: 1.5,
                          }}
                        >
                          {q.caseInfo?.history || "主訴・症状なし"}
                        </Typography>
                      </Tooltip>
                    </CardContent>
                  </Grid>

                  {/* ====== 右側：画像エリア ====== */}
                  <Grid
                    item
                    xs={5}
                    md={5}
                    display={"flex"}
                    justifyContent={"flex-end"}
                    alignItems={"center"}
                    onClick={(event) => {
                      event.stopPropagation(); // Carousel内でクリックイベントを伝播させない
                    }}
                  >
                    {q.resolvedImageUrls && q.resolvedImageUrls.length > 0 ? (
                      <Carousel sx={{ width: "100%", mr: 2 }}>
                        {q.resolvedImageUrls.map((url: string, i: number) => (
                          <CardMedia
                            key={i}
                            component="img"
                            image={url}
                            alt={`case thumbnail ${i + 1}`}
                          />
                        ))}
                      </Carousel>
                    ) : (
                      <div></div>
                    )}
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}